import React, { useState } from 'react';
import { 
  Heart, 
  Brain, 
  Users, 
  ChevronDown,
  Smile,
  Activity,
  Bot,
  MessageCircle,
  Lock,
  Sparkles,
  BookOpen,
  Stethoscope,
  Video,
  FileText,
  Pill,
  UserCheck,
  AlertTriangle,
  MessagesSquare,
  Calendar,
  Bell,
  ShoppingBag,
  UsersRound
} from 'lucide-react';

// Feature data with icons
const categories = [
  {
    id: 'self-awareness',
    title: 'Self-Awareness & Emotional Support',
    description: 'Nurture your inner world with tools designed to help you understand, express, and care for your emotional well-being.',
    icon: Heart,
    color: '#EC4899', // Pink
    bgGradient: 'linear-gradient(135deg, rgba(236, 72, 153, 0.15) 0%, rgba(236, 72, 153, 0.05) 100%)',
    features: [
      { name: 'Mood Tracking', description: 'Log and visualize your emotional patterns', icon: Smile },
      { name: 'MindPulse™', description: 'AI-powered wellness check-ins and alerts', icon: Activity },
      { name: 'AI Companion "Amigo"', description: 'Your empathetic AI friend, available 24/7', icon: Bot },
      { name: 'Just Talk', description: 'Connect with peers who understand', icon: MessageCircle },
      { name: 'Private Mental Vault', description: 'Secure space for your deepest thoughts', icon: Lock },
      { name: 'Quantum Manifestation', description: 'Guided visualization and intention setting', icon: Sparkles },
      { name: 'Content Library', description: 'Curated resources for mental wellness', icon: BookOpen }
    ]
  },
  {
    id: 'professional-care',
    title: 'Professional Care & Clinical Assistance',
    description: 'Access expert support when you need it most, with seamless connections to healthcare professionals.',
    icon: Stethoscope,
    color: '#06B6D4', // Cyan
    bgGradient: 'linear-gradient(135deg, rgba(6, 182, 212, 0.15) 0%, rgba(6, 182, 212, 0.05) 100%)',
    features: [
      { name: 'Find Professional Help', description: 'Browse verified mental health specialists', icon: Stethoscope },
      { name: 'Video Consultation', description: 'Face-to-face sessions from anywhere', icon: Video },
      { name: 'Doctor-Ready Summary', description: 'AI-generated reports for your appointments', icon: FileText },
      { name: 'Medication Management', description: 'Track prescriptions and set reminders', icon: Pill },
      { name: 'Personalized Team', description: 'Build your care team of trusted providers', icon: UserCheck },
      { name: 'Crisis Support', description: '24/7 emergency resources and hotlines', icon: AlertTriangle }
    ]
  },
  {
    id: 'community',
    title: 'Community, Engagement & Life Integration',
    description: 'You\'re not alone. Join a supportive community and integrate wellness seamlessly into your daily life.',
    icon: Users,
    color: '#22C55E', // Green
    bgGradient: 'linear-gradient(135deg, rgba(34, 197, 94, 0.15) 0%, rgba(34, 197, 94, 0.05) 100%)',
    features: [
      { name: 'Mental Health Forums', description: 'Share experiences in moderated discussions', icon: MessagesSquare },
      { name: 'Group Therapy Sessions', description: 'Join live sessions with others on similar journeys', icon: UsersRound },
      { name: 'Calendar Integration', description: 'Sync with Google & Microsoft calendars', icon: Calendar },
      { name: 'Smart Notifications', description: 'Gentle reminders that respect your pace', icon: Bell },
      { name: 'Wellness Store', description: 'Curated products to support your journey', icon: ShoppingBag }
    ]
  }
];

const FeatureCategories = ({ variant = 'default' }) => {
  const [expandedId, setExpandedId] = useState(null);

  const toggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  return (
    <div className="w-full max-w-5xl mx-auto px-4">
      {/* Section Header */}
      <div className="text-center mb-10">
        <h2 
          className="text-2xl sm:text-3xl font-bold text-sunrise-black mb-3"
          style={{ fontFamily: 'Nunito, sans-serif' }}
        >
          Everything You Need for Your Mental Wellness
        </h2>
        <p className="text-sunrise-medium max-w-2xl mx-auto">
          Comprehensive tools and support designed with care, empathy, and your well-being at heart.
        </p>
      </div>

      {/* Category Cards */}
      <div className="space-y-4">
        {categories.map((category) => {
          const isExpanded = expandedId === category.id;
          const IconComponent = category.icon;

          return (
            <div
              key={category.id}
              className="rounded-2xl overflow-hidden transition-all duration-500 ease-out"
              style={{
                background: 'var(--card-bg)',
                border: `1px solid ${isExpanded ? category.color : 'var(--card-border)'}`,
                boxShadow: isExpanded 
                  ? `0 8px 32px ${category.color}20, 0 4px 12px rgba(0,0,0,0.08)`
                  : '0 2px 8px rgba(0,0,0,0.04)'
              }}
              data-testid={`category-card-${category.id}`}
            >
              {/* Card Header - Always Visible */}
              <button
                onClick={() => toggleExpand(category.id)}
                className="w-full px-6 py-5 flex items-center gap-4 text-left transition-all duration-300 hover:bg-white/30 dark:hover:bg-white/5"
                data-testid={`category-toggle-${category.id}`}
              >
                {/* Icon Container */}
                <div 
                  className="flex-shrink-0 w-14 h-14 rounded-xl flex items-center justify-center transition-transform duration-300"
                  style={{ 
                    background: category.bgGradient,
                    transform: isExpanded ? 'scale(1.05)' : 'scale(1)'
                  }}
                >
                  <IconComponent 
                    className="w-7 h-7 transition-all duration-300"
                    style={{ color: category.color }}
                  />
                </div>

                {/* Title and Description */}
                <div className="flex-1 min-w-0">
                  <h3 
                    className="text-lg font-bold text-sunrise-black mb-1 transition-colors duration-300"
                    style={{ 
                      fontFamily: 'Nunito, sans-serif',
                      color: isExpanded ? category.color : undefined
                    }}
                  >
                    {category.title}
                  </h3>
                  <p className="text-sm text-sunrise-medium line-clamp-2">
                    {category.description}
                  </p>
                </div>

                {/* Expand Icon */}
                <div 
                  className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300"
                  style={{ 
                    background: isExpanded ? `${category.color}15` : 'transparent'
                  }}
                >
                  <ChevronDown 
                    className="w-5 h-5 transition-transform duration-300 ease-out"
                    style={{ 
                      color: isExpanded ? category.color : 'var(--text-light)',
                      transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'
                    }}
                  />
                </div>
              </button>

              {/* Expandable Content */}
              <div 
                className="overflow-hidden transition-all duration-500 ease-out"
                style={{
                  maxHeight: isExpanded ? '600px' : '0px',
                  opacity: isExpanded ? 1 : 0
                }}
              >
                <div 
                  className="px-6 pb-6 pt-2"
                  style={{ borderTop: `1px solid ${category.color}20` }}
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {category.features.map((feature, index) => {
                      const FeatureIcon = feature.icon;
                      return (
                        <div
                          key={feature.name}
                          className="flex items-start gap-3 p-3 rounded-xl transition-all duration-300 hover:bg-white/50 dark:hover:bg-white/5 group"
                          style={{
                            animation: isExpanded ? `fadeSlideIn 0.4s ease-out ${index * 0.05}s both` : 'none'
                          }}
                        >
                          <div 
                            className="flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center transition-transform duration-200 group-hover:scale-110"
                            style={{ background: `${category.color}15` }}
                          >
                            <FeatureIcon 
                              className="w-4.5 h-4.5"
                              style={{ color: category.color, width: '18px', height: '18px' }}
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="font-semibold text-sm text-sunrise-black mb-0.5">
                              {feature.name}
                            </h4>
                            <p className="text-xs text-sunrise-light leading-relaxed">
                              {feature.description}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* CSS for animations */}
      <style>{`
        @keyframes fadeSlideIn {
          from {
            opacity: 0;
            transform: translateY(-8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default FeatureCategories;
